import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import numbro from 'numbro';

import '../../components/Templates/Referral/SmartParentsProgramTemplate/SmartParentsProgramTemplate.css';
import { SvgIcon } from '../../components/Molecules';
import { SVGIconType } from '../../constants/svgIcon';
import { getSmartParentsImage } from '../../constants/images';
import {
  cashOutRequirements,
  smartParentPlanPrices,
} from '../../constants/exchangeRates';
import { useSessionStore } from '../../stores/SessionStore';
import { useUserStore } from '../../stores/UserStore';
import { getLocalCurrencyById } from '../../utils/region';

export const ReferralRewardDetails = memo(() => {
  const { t, i18n } = useTranslation();
  const activeRegion = useSessionStore((store) => store.activeRegion);

  const activeUserId = useSessionStore((store) => store.activeUserId);
  const userProfile = useUserStore((state) =>
    state.getProfile(activeUserId ?? ''),
  );
  const currency = useMemo(() => {
    return userProfile?.localCurrency ?? getLocalCurrencyById(activeRegion);
  }, [userProfile, activeRegion]);

  const formatMoney = (m: number) => {
    return numbro(m).format({ thousandSeparated: true });
  };

  return (
    <div className="smart-parents-program">
      <div className="centered-container-nobg">
        <div className="container-purple">
          <SvgIcon
            name={SVGIconType.Referral}
            className={'title-icon'}
            stroke={'#ffffff'}
            fill={'#ffffff'}
            color={'#ffffff'}
          />
          <p className="text-white-l">{t('Double Referral Reward Scheme')}</p>
          <p className="text-white-s">
            {t(
              'With the Smart Parents Program, we offer a generous referral reward scheme. You might easily earn enough to cover your program admission fee, or even more!',
            )}
          </p>
        </div>
        <div className="container-white">
          <div className="container-detail-blue">
            <SvgIcon
              name={SVGIconType.Person}
              className={'title-icon-person1'}
              stroke={'#ffffff'}
              // fill={'#4a6db0'}
              // color={'#4a6db0'}
            />
            <p className="text-blue1-l">{t('Tier 1 Referral')}</p>
            <p className="text-black-m">
              {t(
                'After completing the admission payment, you can refer new participants. New participant who uses your referral code and completes the admission payment, will become your Tier 1 Referrals.',
              )}
            </p>
            <p className="text-black-m">
              {t(
                'For each Tier 1 Referral, earn {firstYear} JOY Cash on their 1st year payment and {afterwards} on each renewal.',
                {
                  firstYear: `${currency}${formatMoney(
                    smartParentPlanPrices[currency].tier1PurchaseReward,
                  )}`,
                  afterwards: `${currency}${formatMoney(
                    smartParentPlanPrices[currency].tier1RepurchaseReward,
                  )}`,
                },
              )}
            </p>
          </div>
          <div className="container-detail-blue-light">
            <SvgIcon
              name={SVGIconType.Person}
              className={'title-icon-person2'}
              stroke={'#ffffff'}
              // fill={'#4a6db0'}
              // color={'#4a6db0'}
            />
            <p className="text-blue2-l">{t('Tier 2 Referral')}</p>
            <p className="text-black-m">
              {t(
                'Participants referred by your Tier 1 Referrals become your Tier 2 Referrals.',
              )}
            </p>
            <p className="text-black-m">
              {t(
                'For each Tier 2 Referral, earn {firstYear} JOY Cash on their 1st year payment and {afterwards} on each renewal.',
                {
                  firstYear: `${currency}${formatMoney(
                    smartParentPlanPrices[currency].tier2PurchaseReward,
                  )}`,
                  afterwards: `${currency}${formatMoney(
                    smartParentPlanPrices[currency].tier2RepurchaseReward,
                  )}`,
                },
              )}
            </p>
          </div>
          <div className="container-detail-red">
            <SvgIcon
              name={SVGIconType.Cash}
              className={'title-icon-person2'}
              stroke={'#1ca798'}
              // fill={'#4a6db0'}
              // color={'#4a6db0'}
            />
            <p className="text-green-l">{t('Cashing Out JOY Cash')}</p>
            <p className="text-black-m">
              {t(
                'JOY Cash can be redeemed once you accumulate {minimum} or more.',
                {
                  minimum: `${currency}${formatMoney(
                    cashOutRequirements[currency] ?? 0,
                  )}`,
                },
              )}
            </p>
            <p className="text-black-m">
              {t(
                'Redemption requests must be submitted one month in advance and will be processed on the 15th of each month.',
              )}
            </p>
            <p className="text-black-m">
              {t(
                'Your reward will be distributed over two months. You will receive 50% of your reward in the first month and the remaining 50% in the following month.',
              )}
            </p>
          </div>
        </div>
      </div>
      <div className="centered-container-nobg">
        <div className="container-purple-light">
          <p className="text-green-l">{t('Referral Reward Example')}</p>
          <img
            src={getSmartParentsImage('img3', i18n.language)}
            className="img3"
            alt="JOYAJOY Banner"
          />
          <p className="text-blue1-l">{t('Tier 1 Referral Reward')}</p>
          <p className="text-black-m">
            {t(
              "If you have three Tier 1 Referrals, you'll earn {base} x 3 = {total} JOY Cash.",
              {
                base: `${currency}${formatMoney(
                  smartParentPlanPrices[currency].tier1PurchaseReward,
                )}`,
                total: `${currency}${formatMoney(
                  smartParentPlanPrices[currency].tier1PurchaseReward * 3,
                )}`,
              },
            )}
          </p>
          <p className="text-blue2-l">{t('Tier 2 Referral Reward')}</p>
          <p className="text-black-m">
            {t(
              "If each of these Tier 1 Referrals refers 3 users, you'll have 9 Tier 2 Referrals, rewarding you with {base} x 9 = {total} JOY Cash.",
              {
                base: `${currency}${formatMoney(
                  smartParentPlanPrices[currency].tier2PurchaseReward,
                )}`,
                total: `${currency}${formatMoney(
                  smartParentPlanPrices[currency].tier2PurchaseReward * 9,
                )}`,
              },
            )}
          </p>
          <p className="text-black-m">
            {t(
              'As a result, you will have a total of {sum} in JOY Cash, enough to cover your own program fee plus considerable extra earnings!',
              {
                sum: `${currency}${formatMoney(
                  smartParentPlanPrices[currency].tier1PurchaseReward * 3 +
                    smartParentPlanPrices[currency].tier2PurchaseReward * 9,
                )}`,
              },
            )}
          </p>
        </div>
      </div>
      <div className="centered-container-nobg">
        <div className="container-green-light">
          <SvgIcon
            name={SVGIconType.Cash}
            className={'title-icon-person2'}
            stroke={'#1ca798'}
            // fill={'#4a6db0'}
            // color={'#4a6db0'}
          />
          <p className="text-green-l">{t('Cashing Out Example')}</p>
          <p className="text-black-m">
            {t(
              'If you submit a cash out request on January 18, you will receive the funds on March 15, as requests must be submitted one month in advance.',
            )}
          </p>
          <p className="text-black-m">
            {t(
              'Please note that the cash out will be distributed over two months. If you request {cur}1,000, you will receive {cur}500 in the first month and the remaining {cur}500 in the following month.',
              { cur: currency },
            )}
          </p>
          <p className="text-black-m">
            {t(
              'Please ensure you submit your request on time to receive your cash promptly.',
            )}
          </p>
        </div>
      </div>
    </div>
  );
});
